<template>
  <v-container fluid class="pa-4">
    <v-card class="elevation-2">
      <v-toolbar color="deep-orange darken-2" dark dense>
        <v-icon left>call_made</v-icon>
        <v-toolbar-title>Borç Yönetimi</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon :loading="loading" @click="loadAll">
          <v-icon>refresh</v-icon>
        </v-btn>
        <v-btn color="white" flat class="inv-btn ma-0 ml-2" @click="openLend">
          <v-icon left small>call_made</v-icon>
          Borç Ver
        </v-btn>
      </v-toolbar>

      <v-progress-linear v-if="loading" indeterminate color="deep-orange darken-2" class="ma-0"></v-progress-linear>

      <v-card-text>
        <div class="inv-hint">
          Envanteri birine borç/emanet olarak verir; ana envanterden düşer. Geri alındığında stok envantere geri eklenir.
        </div>

        <v-layout wrap align-center class="mb-3">
          <v-flex xs12 sm6 md4 class="pr-2">
            <v-text-field
              v-model="search"
              label="Ara (borçlu / ürün / seri)"
              prepend-icon="search"
              single-line
              hide-details
              clearable
            ></v-text-field>
          </v-flex>
        </v-layout>

        <v-layout wrap class="inv-stats">
          <v-flex xs6 sm3 class="pr-2">
            <div class="inv-stat inv-stat--grey">
              <div class="inv-stat-label">Kayıt</div>
              <div class="inv-stat-value">{{ filteredLines.length }}</div>
            </div>
          </v-flex>
          <v-flex xs6 sm3 class="pr-2">
            <div class="inv-stat inv-stat--amber">
              <div class="inv-stat-label">Aktif Borç</div>
              <div class="inv-stat-value">{{ activeCount }}</div>
            </div>
          </v-flex>
          <v-flex xs6 sm3 class="pr-2">
            <div class="inv-stat inv-stat--purple">
              <div class="inv-stat-label">Aktif Toplam Has</div>
              <div class="inv-stat-value">{{ activeHasText }} <span class="inv-stat-unit">gr</span></div>
            </div>
          </v-flex>
          <v-flex xs6 sm3>
            <div class="inv-stat inv-stat--green">
              <div class="inv-stat-label">Geri Alınan</div>
              <div class="inv-stat-value">{{ returnedCount }}</div>
            </div>
          </v-flex>
        </v-layout>

        <div v-if="!filteredLines.length && !loading" class="inv-empty">
          <v-icon large color="grey lighten-1">call_made</v-icon>
          <div>Henüz borç kaydı yok.</div>
        </div>
        <div v-else>
          <div class="inv-card" :class="lineCardClass(line)" v-for="line in filteredLines" :key="line.id">
            <div class="inv-icon" :class="typeIconClass(line.inventoryType)">
              <v-icon color="white" small>{{ typeIcon(line.inventoryType) }}</v-icon>
            </div>

            <div class="inv-card-l">
              <div class="inv-head">
                <span class="inv-title">{{ line.borrowerName }}</span>
                <span class="inv-chip" :class="typeChipClass(line.inventoryType)">{{ line.inventoryTypeName }}</span>
                <span class="inv-chip ml-2" :class="line.status === 0 ? 'inv-chip--wait' : 'inv-chip--done'">
                  {{ line.statusName }}
                </span>
              </div>

              <div class="inv-items">
                <span class="inv-item">{{ line.productName }}{{ variantSuffix(line) }} × {{ line.quantity }}</span>
                <span v-if="line.serialNumber" class="inv-item">Seri: {{ line.serialNumber }}</span>
              </div>

              <div v-if="line.description" class="inv-note">“{{ line.description }}”</div>

              <div class="inv-meta">
                <v-icon x-small color="blue-grey lighten-2">event</v-icon>
                {{ formatDate(line.lentDate) }}
                <span class="inv-dot">·</span>
                <v-icon x-small color="blue-grey lighten-2">layers</v-icon>
                {{ line.quantity }} adet
              </div>
            </div>

            <div class="inv-card-r">
              <div class="inv-value">
                <div class="inv-value-num">{{ formatHas(line.hasAmount) }}</div>
                <div class="inv-value-label">gr has</div>
              </div>
              <v-btn
                v-if="line.status === 0"
                color="red darken-1"
                dark
                class="inv-btn ma-0 ml-3"
                @click="openReturn(line)"
              >
                <v-icon small left>undo</v-icon>
                Geri Al
              </v-btn>
            </div>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <!-- ========== BORÇ VER DIALOG ========== -->
    <v-dialog v-model="lendDialog" max-width="560" persistent>
      <v-card>
        <v-card-title class="headline deep-orange darken-2 white--text">
          <v-icon left color="white">call_made</v-icon>
          Borç Ver
        </v-card-title>
        <v-card-text class="pa-4">
          <v-text-field v-model="lendForm.borrowerName" label="Kime verildi (borçlu)" placeholder="Örn. Ahmet Y. (kuyumcu)" prepend-icon="person" autofocus></v-text-field>
          <v-textarea v-model="lendForm.description" label="Açıklama" prepend-icon="notes" rows="2" auto-grow></v-textarea>

          <v-tabs v-model="lendTab" grow class="mb-2">
            <v-tab>Ziynet / Külçe / Diğer</v-tab>
            <v-tab>Takı</v-tab>
          </v-tabs>

          <div v-if="lendTab === 0">
            <v-select
              v-model="lendForm.inventoryItemId"
              :items="adetItems"
              item-text="label"
              item-value="id"
              label="Ürün"
              prepend-icon="category"
            ></v-select>
            <div v-if="selectedInventoryItem" class="caption grey--text mb-2">
              Ana envanterde boşta: <strong>{{ availableOf(selectedInventoryItem) }}</strong> adet
            </div>
            <v-text-field v-model.number="lendForm.quantity" label="Adet" type="number" min="1" prepend-icon="plus_one"></v-text-field>
          </div>

          <div v-else>
            <v-text-field
              v-model="lendForm.barcode"
              label="Takı Barkodu / Seri No"
              prepend-icon="qr_code_scanner"
              hint="Sertifikadaki barkodu okutun veya yazın"
              persistent-hint
              @keyup.enter="saveLend"
            ></v-text-field>
          </div>
        </v-card-text>
        <v-card-actions class="pa-3">
          <v-spacer></v-spacer>
          <v-btn flat color="grey" @click="lendDialog = false">İptal</v-btn>
          <v-btn color="deep-orange darken-2" dark class="inv-btn" :loading="lendSaving" @click="saveLend">Borç Ver</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ========== GERİ AL DIALOG (adet) ========== -->
    <v-dialog v-model="returnDialog" max-width="460" persistent>
      <v-card>
        <v-card-title class="headline red darken-1 white--text">
          <v-icon left color="white">undo</v-icon>
          Ana Envantere Geri Al
        </v-card-title>
        <v-card-text class="pa-4">
          <div class="mb-2">
            <strong>{{ returnTarget && returnTarget.borrowerName }}</strong> →
            {{ returnTarget && returnTarget.productName }}{{ variantSuffix(returnTarget) }}
          </div>
          <v-text-field
            v-model.number="returnQuantity"
            label="Geri alınacak adet"
            type="number"
            min="1"
            prepend-icon="plus_one"
            :max="returnTarget ? returnTarget.quantity : 1"
          ></v-text-field>
          <div class="caption grey--text">Borçta: {{ returnTarget ? returnTarget.quantity : 0 }} adet</div>
        </v-card-text>
        <v-card-actions class="pa-3">
          <v-spacer></v-spacer>
          <v-btn flat color="grey" @click="returnDialog = false">İptal</v-btn>
          <v-btn color="red darken-1" dark class="inv-btn" :loading="returnSaving" @click="confirmReturn">Geri Al</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
export default {
  name: "BorcYonetimi",
  data: () => ({
    loading: false,
    lines: [],
    inventoryItems: [],
    zimmetLines: [],
    search: "",
    headers: [
      { text: "Borçlu", value: "borrowerName" },
      { text: "Tip", value: "inventoryTypeName" },
      { text: "Ürün", value: "productName" },
      { text: "Seri No", value: "serialNumber" },
      { text: "Adet", value: "quantity", align: "right" },
      { text: "Has", value: "hasAmount", align: "right" },
      { text: "Tarih", value: "lentDate" },
      { text: "Açıklama", value: "description" },
      { text: "Durum", value: "status" },
      { text: "", value: "actions", sortable: false },
    ],

    lendDialog: false,
    lendSaving: false,
    lendTab: 0,
    lendForm: { borrowerName: "", description: "", inventoryItemId: null, quantity: 1, barcode: "" },

    returnDialog: false,
    returnSaving: false,
    returnTarget: null,
    returnQuantity: 1,
  }),

  computed: {
    adetItems() {
      return this.inventoryItems
        .filter((i) => i.inventoryType !== 0) // 0 = Takı
        .map((i) => ({
          ...i,
          label: `${i.inventoryTypeName} - ${i.productName}${this.variantSuffix(i)} (${i.weight} gr, ${i.purityLabel}) — boşta ${this.availableOf(i)}`,
        }));
    },
    selectedInventoryItem() {
      return this.inventoryItems.find((i) => i.id === this.lendForm.inventoryItemId) || null;
    },
    filteredLines() {
      const q = (this.search || "").toLocaleLowerCase("tr");
      if (!q) return this.lines;
      return this.lines.filter((l) =>
        [l.borrowerName, l.productName, l.serialNumber, l.inventoryTypeName, l.description]
          .filter(Boolean)
          .some((v) => v.toLocaleLowerCase("tr").includes(q))
      );
    },
    activeCount() {
      return this.filteredLines.filter((l) => l.status === 0).length;
    },
    returnedCount() {
      return this.filteredLines.filter((l) => l.status !== 0).length;
    },
    activeHas() {
      return this.filteredLines
        .filter((l) => l.status === 0)
        .reduce((s, l) => s + (Number(l.hasAmount) || 0), 0);
    },
    activeHasText() {
      return Number(this.activeHas).toLocaleString("tr-TR", { maximumFractionDigits: 4 });
    },
  },

  created() {
    this.loadAll();
  },

  methods: {
    variantSuffix(i) {
      if (!i) return "";
      const parts = [i.kulp, i.dateType].filter(Boolean);
      return parts.length ? ` [${parts.join(" / ")}]` : "";
    },
    typeColor(t) {
      if (t === 0) return "teal";
      if (t === 1) return "amber darken-2";
      return "blue-grey darken-1";
    },
    typeIcon(t) {
      if (t === 0) return "diamond";
      if (t === 1) return "monetization_on";
      if (t === 2) return "view_in_ar";
      return "category";
    },
    typeIconClass(t) {
      if (t === 0) return "inv-icon--teal";
      if (t === 1) return "inv-icon--amber";
      if (t === 2) return "inv-icon--brown";
      return "inv-icon--grey";
    },
    typeChipClass(t) {
      if (t === 0) return "inv-chip--teal";
      if (t === 1) return "inv-chip--brown";
      if (t === 2) return "inv-chip--purple";
      return "inv-chip--grey";
    },
    lineCardClass(line) {
      if (!line) return "";
      return line.status === 0 ? "inv-card--wait" : "inv-card--done";
    },
    formatHas(v) {
      if (v == null || Number(v) === 0) return "-";
      return Number(v).toLocaleString("tr-TR", { maximumFractionDigits: 4 });
    },
    formatDate(d) {
      if (!d) return "-";
      return new Date(d).toLocaleDateString("tr-TR");
    },
    // boşta = toplam Quantity − zimmetli (borç zaten Quantity'i düşürdüğü için canlı doğru)
    availableOf(item) {
      if (!item) return 0;
      const assigned = this.zimmetLines
        .filter((l) => l.inventoryItemId === item.id)
        .reduce((s, l) => s + (l.quantity || 0), 0);
      return item.quantity - assigned;
    },

    async loadAll() {
      this.loading = true;
      try {
        const [linesRes, itemsRes, zimmetRes] = await Promise.all([
          this.$store.dispatch("inventory/getBorclar"),
          this.$store.dispatch("inventory/getInventoryItems"),
          this.$store.dispatch("inventory/getAllZimmets"),
        ]);
        this.lines = linesRes || [];
        this.inventoryItems = itemsRes || [];
        this.zimmetLines = zimmetRes || [];
      } catch (e) {
        this.notify("Borç verileri yüklenemedi: " + this.errMsg(e));
      } finally {
        this.loading = false;
      }
    },

    openLend() {
      this.lendForm = { borrowerName: "", description: "", inventoryItemId: null, quantity: 1, barcode: "" };
      this.lendTab = 0;
      this.lendDialog = true;
    },

    async saveLend() {
      const borrower = (this.lendForm.borrowerName || "").trim();
      if (!borrower) {
        this.notify("Borçlu adı girin.");
        return;
      }
      let dto;
      if (this.lendTab === 1) {
        const code = (this.lendForm.barcode || "").trim().replace(/\*/g, "-"); // okuyucu "-"yi "*" gönderebilir
        if (!code) { this.notify("Takı barkodu okutun."); return; }
        dto = { borrowerName: borrower, description: this.lendForm.description, barcode: code };
      } else {
        if (!this.lendForm.inventoryItemId) { this.notify("Ürün seçin."); return; }
        if (!this.lendForm.quantity || this.lendForm.quantity <= 0) { this.notify("Adet 0'dan büyük olmalı."); return; }
        dto = {
          borrowerName: borrower,
          description: this.lendForm.description,
          inventoryItemId: this.lendForm.inventoryItemId,
          quantity: this.lendForm.quantity,
        };
      }
      this.lendSaving = true;
      try {
        const res = await this.$store.dispatch("inventory/borcLend", dto);
        if (res && res.success === false) { this.notify(res.message); return; }
        if (this.lendTab === 1) this.lendForm.barcode = "";
        else this.lendDialog = false;
        this.notify((res && res.message) || "Borç verildi.");
        await this.loadAll();
      } catch (e) {
        this.notify("Hata: " + this.errMsg(e));
      } finally {
        this.lendSaving = false;
      }
    },

    openReturn(line) {
      this.returnTarget = line;
      if (line.inventoryType === 0) {
        this.doReturn(line, 0);
        return;
      }
      this.returnQuantity = line.quantity;
      this.returnDialog = true;
    },

    async confirmReturn() {
      if (!this.returnTarget) return;
      if (!this.returnQuantity || this.returnQuantity <= 0) { this.notify("Adet 0'dan büyük olmalı."); return; }
      await this.doReturn(this.returnTarget, this.returnQuantity);
      this.returnDialog = false;
    },

    async doReturn(line, quantity) {
      this.returnSaving = true;
      try {
        const res = await this.$store.dispatch("inventory/borcReturn", { borcId: line.id, quantity });
        if (res && res.success === false) { this.notify(res.message); return; }
        this.notify((res && res.message) || "Ana envantere geri alındı.");
        await this.loadAll();
      } catch (e) {
        this.notify("Hata: " + this.errMsg(e));
      } finally {
        this.returnSaving = false;
      }
    },

    errMsg(e) {
      return (e && e.response && e.response.data && e.response.data.message) || (e && e.message) || "Bilinmeyen hata";
    },
    notify(msg) {
      this.$store.state.dialog = true;
      this.$store.state.message = msg;
    },
  },
};
</script>
